import { cn } from "@/lib/utils";
import { useState } from "react";
import type { StatefulDiaryT } from "../../../../../types/database/diaries";
import type { StatefulObservationViewT } from "../../../../../types/database/observations";
import type { StatefulTrackerViewT } from "../../../../../types/database/trackers";
import EventInHistory from "../../../../events/components/EventInHistory";
import ObservationInHistory from "../../../../observations/components/ObservationInHistory";
import TrackerInHistory from "../../../../trackers/components/TrackerInHistory";

interface Props {
	diary: StatefulDiaryT | null | undefined;
}

// Define types for our combined items
type CombinedItem =
	| { type: "tracker"; item: StatefulTrackerViewT; startDate: string }
	| { type: "observation"; item: StatefulObservationViewT; startDate: string }
	| {
			type: "event";
			item: StatefulDiaryT["events_view"][0];
			startDate: string;
	  };

const HistoryByTracker = (props: Props) => {
	const [isEditingEvent, setIsEditingEvent] = useState(false);
	const [sortAscending, setSortAscending] = useState(false);

	// After the null check, we can safely use diary without TypeScript errors
	const diary = props.diary;

	// Combine trackers, observations, and events into a single array with type information
	const combinedItems: CombinedItem[] = [
		...(diary?.trackers_view?.map((tracker) => ({
			type: "tracker" as const,
			item: tracker as unknown as StatefulTrackerViewT,
			startDate: tracker.shared_attributes?.start_date || "",
		})) || []),
		...(diary?.observations_view?.map((observation) => ({
			type: "observation" as const,
			item: observation as unknown as StatefulObservationViewT,
			startDate: observation.shared_attributes?.start_date || "",
		})) || []),
		...(diary?.events_view?.map((event) => ({
			type: "event" as const,
			item: event,
			startDate: event.shared_attributes?.reference_date || "",
		})) || []),
	].sort((a, b) => {
		// First sort by start date
		const dateComparison = a.startDate.localeCompare(b.startDate);
		if (dateComparison !== 0) return dateComparison;

		// If dates are the same, put trackers before observations, and observations before events
		if (
			a.type === "tracker" &&
			(b.type === "observation" || b.type === "event")
		)
			return -1;
		if (a.type === "observation" && b.type === "event") return -1;
		if (
			a.type === "event" &&
			(b.type === "tracker" || b.type === "observation")
		)
			return 1;
		if (a.type === "observation" && b.type === "tracker") return 1;

		return 0;
	});

	return (
		<div className={cn("flex flex-col gap-y-4 w-full pb-8")}>
			{combinedItems.map((item, index) => {
				if (item.type === "tracker") {
					const tracker = item.item;
					const events = diary.events_view.filter((event) =>
						(tracker.events as { event_id: string }[])
							?.map((e) => e.event_id)
							.includes(event.event_id),
					);

					const actions = diary.actions_view;

					return (
						<TrackerInHistory
							key={tracker.tracker_id}
							tracker={tracker}
							index={index}
							actions={actions}
							sortAscending={sortAscending}
						/>
					);
				}

				if (item.type === "observation") {
					// This is an observation
					const observation = item.item;
					const actions = diary.actions_view;

					return (
						<ObservationInHistory
							key={observation.observation_id}
							observation={observation}
							index={index}
							actions={actions}
							sortAscending={sortAscending}
							setSortAscending={setSortAscending}
						/>
					);
				}

				// This is an event
				const event = item.item;
				return (
					<EventInHistory
						key={event.event_id}
						event={event}
						index={index}
						isEditingEvent={isEditingEvent}
						setIsEditingEvent={setIsEditingEvent}
					/>
				);
			})}
		</div>
	);
};

export default HistoryByTracker;
